import React, { useState } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  FileSpreadsheet, 
  FileText, 
  Calendar, 
  Clock, 
  User, 
  CheckCircle2, 
  AlertTriangle, 
  AlertOctagon, 
  PlayCircle, 
  Layers, 
  Edit3, 
  Trash2, 
  ExternalLink, 
  Eye, 
  Kanban,
  Building2,
  DollarSign,
  Briefcase,
  TrendingUp,
  X
} from 'lucide-react';
import { ProjectItem, ProjectStatus, ProjectPriority, ProjectTask } from '../types.ts';
import { exportProjectToExcel, exportAllProjectsSummaryToExcel } from '../utils/projectExcelExport.ts';
import { exportProjectToPDF, exportAllProjectsSummaryToPDF } from '../utils/projectPdfExport.ts';

interface ProjectsListSubpageProps {
  projects: ProjectItem[];
  businessUnitName: string;
  onSelectProjectForGantt: (projectId: string) => void;
  onSaveProject: (project: ProjectItem) => void;
  onDeleteProject: (projectId: string) => void;
}

export const ProjectsListSubpage: React.FC<ProjectsListSubpageProps> = ({
  projects,
  businessUnitName,
  onSelectProjectForGantt,
  onSaveProject,
  onDeleteProject
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modals
  const [selectedProjectDetail, setSelectedProjectDetail] = useState<ProjectItem | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Partial<ProjectItem>>({});

  // Filtered list
  const filteredProjects = projects.filter(prj => {
    const matchesSearch = 
      prj.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prj.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prj.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prj.manager.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'ALL' || prj.status === statusFilter;
    const matchesCategory = categoryFilter === 'ALL' || prj.category === categoryFilter;

    return matchesSearch && matchesStatus && matchesCategory;
  });

  // KPI Calculations
  const totalCount = projects.length;
  const onTimeCount = projects.filter(p => p.status === 'EN_TIEMPO' || p.status === 'COMPLETADO').length;
  const atRiskCount = projects.filter(p => p.status === 'EN_RIESGO' || p.status === 'RETRASADO').length;
  const completedCount = projects.filter(p => p.status === 'COMPLETADO').length;
  const avgProgress = totalCount > 0 
    ? Math.round(projects.reduce((acc, p) => acc + p.progressPercent, 0) / totalCount)
    : 0;

  const handleOpenNewProject = () => {
    const nextNum = projects.length + 1;
    setEditingProject({
      id: `PRJ-${Date.now()}`,
      code: `PRJ-BAJ-${new Date().getFullYear()}-${nextNum.toString().padStart(2, '0')}`,
      name: '',
      businessUnit: businessUnitName,
      clientName: '',
      category: 'MANTENIMIENTO',
      manager: '',
      sponsor: '',
      startDate: new Date().toISOString().split('T')[0],
      targetEndDate: new Date(Date.now() + 45 * 86400000).toISOString().split('T')[0],
      status: 'POR_INICIAR',
      priority: 'ALTA',
      budgetTotal: 150000,
      budgetCurrency: 'MXN',
      description: '',
      scopeObjectives: '',
      progressPercent: 0,
      tasks: []
    });
    setIsEditModalOpen(true);
  };

  const handleSaveProjectForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProject.name || !editingProject.code) {
      window.showAppAlert('Por favor ingrese el código y nombre del proyecto.', 'Campos Requeridos');
      return;
    }

    const saved: ProjectItem = {
      id: editingProject.id || `PRJ-${Date.now()}`,
      code: editingProject.code,
      name: editingProject.name,
      businessUnit: editingProject.businessUnit || businessUnitName,
      costCenterId: editingProject.costCenterId,
      costCenterName: editingProject.costCenterName || businessUnitName,
      clientName: editingProject.clientName || 'Planta Operativa',
      category: (editingProject.category as any) || 'MANTENIMIENTO',
      manager: editingProject.manager || 'Líder de Proyecto',
      sponsor: editingProject.sponsor || '',
      startDate: editingProject.startDate || new Date().toISOString().split('T')[0],
      targetEndDate: editingProject.targetEndDate || new Date().toISOString().split('T')[0],
      actualEndDate: editingProject.actualEndDate,
      status: (editingProject.status as ProjectStatus) || 'POR_INICIAR',
      priority: (editingProject.priority as ProjectPriority) || 'ALTA',
      budgetTotal: editingProject.budgetTotal,
      budgetCurrency: editingProject.budgetCurrency || 'MXN',
      description: editingProject.description || '',
      scopeObjectives: editingProject.scopeObjectives || '',
      progressPercent: editingProject.progressPercent ?? 0,
      tasks: editingProject.tasks || [],
      createdAt: editingProject.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onSaveProject(saved);
    setIsEditModalOpen(false);
    setEditingProject({});
  };

  const getStatusBadge = (status: ProjectStatus) => {
    switch (status) {
      case 'COMPLETADO':
        return <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-950/70 text-emerald-300 border border-emerald-500/40 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> COMPLETADO</span>;
      case 'EN_TIEMPO':
        return <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-950/40 text-emerald-400 border border-emerald-500/30 flex items-center gap-1"><PlayCircle className="w-3 h-3 text-emerald-400" /> EN TIEMPO</span>;
      case 'EN_RIESGO':
        return <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-950/60 text-amber-300 border border-amber-500/40 flex items-center gap-1 animate-pulse"><AlertTriangle className="w-3 h-3 text-amber-400" /> EN RIESGO</span>;
      case 'RETRASADO':
        return <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-rose-950/70 text-rose-300 border border-rose-500/40 flex items-center gap-1 animate-pulse"><AlertOctagon className="w-3 h-3 text-rose-400" /> RETRASADO</span>;
      case 'POR_INICIAR':
        return <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-800 text-slate-300 border border-slate-700 flex items-center gap-1"><Clock className="w-3 h-3 text-slate-400" /> POR INICIAR</span>;
      case 'PAUSADO':
        return <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-purple-950/60 text-purple-300 border border-purple-500/30 flex items-center gap-1">PAUSADO</span>;
    }
  };

  const getPriorityBadge = (priority: ProjectPriority) => {
    switch (priority) {
      case 'CRITICA':
        return <span className="text-[9px] font-mono font-bold text-red-400 bg-red-950/50 px-1.5 py-0.5 rounded border border-red-500/30">CRÍTICA</span>;
      case 'ALTA':
        return <span className="text-[9px] font-mono font-bold text-amber-400 bg-amber-950/50 px-1.5 py-0.5 rounded border border-amber-500/30">ALTA</span>;
      case 'MEDIA':
        return <span className="text-[9px] font-mono font-bold text-sky-400 bg-sky-950/50 px-1.5 py-0.5 rounded border border-sky-500/30">MEDIA</span>;
      case 'BAJA':
        return <span className="text-[9px] font-mono font-bold text-slate-400 bg-slate-800 px-1.5 py-0.5 rounded border border-slate-700">BAJA</span>;
    }
  };

  return (
    <div className="space-y-6">
      
      {/* TOP KPI SUMMARY HEADER */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="bg-[#16171d] border border-[#272935] rounded-2xl p-3.5 space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[10px] font-bold uppercase tracking-wider font-mono">Total Proyectos</span>
            <Layers className="w-4 h-4 text-sky-400" />
          </div>
          <div className="text-2xl font-black text-white font-mono">{totalCount}</div>
          <p className="text-[10px] text-slate-400 truncate">Sede {businessUnitName}</p>
        </div>

        <div className="bg-[#16171d] border border-emerald-500/20 rounded-2xl p-3.5 space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[10px] font-bold uppercase tracking-wider font-mono text-emerald-400">En Tiempo</span>
            <PlayCircle className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="text-2xl font-black text-emerald-400 font-mono">{onTimeCount}</div>
          <p className="text-[10px] text-slate-400">Cumpliendo cronograma</p>
        </div>

        <div className="bg-[#16171d] border border-amber-500/20 rounded-2xl p-3.5 space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[10px] font-bold uppercase tracking-wider font-mono text-amber-400">En Riesgo / Retraso</span>
            <AlertTriangle className="w-4 h-4 text-amber-400" />
          </div>
          <div className="text-2xl font-black text-amber-400 font-mono">{atRiskCount}</div>
          <p className="text-[10px] text-slate-400">Requiere intervención</p>
        </div>

        <div className="bg-[#16171d] border border-cyan-500/20 rounded-2xl p-3.5 space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[10px] font-bold uppercase tracking-wider font-mono text-cyan-400">Completados</span>
            <CheckCircle2 className="w-4 h-4 text-cyan-400" />
          </div>
          <div className="text-2xl font-black text-cyan-400 font-mono">{completedCount}</div>
          <p className="text-[10px] text-slate-400">Entregados con éxito</p>
        </div>

        <div className="col-span-2 sm:col-span-1 bg-[#16171d] border border-[#272935] rounded-2xl p-3.5 space-y-1">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[10px] font-bold uppercase tracking-wider font-mono">Avance Promedio</span>
            <TrendingUp className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="text-2xl font-black text-emerald-400 font-mono">{avgProgress}%</div>
          <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
            <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${avgProgress}%` }}></div>
          </div>
        </div>
      </div>

      {/* FILTER & ACTIONS BAR */}
      <div className="bg-[#16171d] border border-[#272935] rounded-2xl p-4 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 shadow-md">
        
        {/* Left: Search & Filter Dropdowns */}
        <div className="flex flex-wrap items-center gap-2.5 flex-1">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por código, nombre, cliente o PM..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#101115] border border-[#2c2f3e] rounded-xl pl-9 pr-3 py-2 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#101115] border border-[#2c2f3e] rounded-xl px-3 py-2 text-xs text-slate-200 font-medium cursor-pointer"
          >
            <option value="ALL">Todos los Estados</option>
            <option value="EN_TIEMPO">En Tiempo</option>
            <option value="EN_RIESGO">En Riesgo</option>
            <option value="RETRASADO">Retrasado</option>
            <option value="COMPLETADO">Completado</option>
            <option value="POR_INICIAR">Por Iniciar</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-[#101115] border border-[#2c2f3e] rounded-xl px-3 py-2 text-xs text-slate-200 font-medium cursor-pointer"
          >
            <option value="ALL">Todas las Categorías</option>
            <option value="MANTENIMIENTO">Mantenimiento</option>
            <option value="AUTOMATIZACION">Automatización</option>
            <option value="OBRA_CIVIL">Obra Civil</option>
            <option value="HSEQ">HSEQ / Seguridad</option>
            <option value="SERVICIOS_ESPECIALIZADOS">Servicios Especializados</option>
          </select>
        </div>

        {/* Right: Export buttons & New Project */}
        <div className="flex flex-wrap items-center gap-2">
          {/* View mode toggle */}
          <div className="flex items-center bg-[#101115] border border-[#2c2f3e] rounded-xl p-0.5">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                viewMode === 'grid' ? 'bg-[#2a2d3c] text-white' : 'text-slate-400 hover:text-white'
              }`}
              title="Vista de Cuadrícula"
            >
              Tarjetas
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                viewMode === 'table' ? 'bg-[#2a2d3c] text-white' : 'text-slate-400 hover:text-white'
              }`}
              title="Vista de Tabla Ejecutiva"
            >
              Tabla
            </button>
          </div>

          {/* Export general summary buttons */}
          <button
            onClick={() => exportAllProjectsSummaryToExcel(projects, businessUnitName)}
            className="px-3 py-2 rounded-xl bg-emerald-950/60 hover:bg-emerald-900/80 text-emerald-300 border border-emerald-600/40 text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer"
            title="Descargar Resumen Completo en Excel"
          >
            <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" />
            <span className="hidden sm:inline">Excel Resumen</span>
          </button>

          <button
            onClick={() => exportAllProjectsSummaryToPDF(projects, businessUnitName)}
            className="px-3 py-2 rounded-xl bg-rose-950/60 hover:bg-rose-900/80 text-rose-300 border border-rose-600/40 text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer"
            title="Descargar Resumen Completo en PDF"
          >
            <FileText className="w-3.5 h-3.5 text-rose-400" />
            <span className="hidden sm:inline">PDF Resumen</span>
          </button>

          <button
            onClick={handleOpenNewProject}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white text-xs font-bold flex items-center gap-1.5 shadow-lg shadow-red-600/20 transition-all cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Nuevo Proyecto</span>
          </button>
        </div>

      </div>

      {/* PROJECTS LIST CONTAINER */}
      {filteredProjects.length === 0 ? (
        <div className="bg-[#16171d] border border-[#272935] rounded-2xl p-12 text-center space-y-3">
          <Briefcase className="w-12 h-12 text-slate-500 mx-auto opacity-50" />
          <h4 className="text-base font-bold text-white">No se encontraron proyectos</h4>
          <p className="text-xs text-slate-400 max-w-sm mx-auto">
            No hay proyectos registrados que coincidan con los filtros de búsqueda actuales.
          </p>
          <button
            onClick={handleOpenNewProject}
            className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Crear Primer Proyecto</span>
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        
        // GRID VIEW CARDS
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredProjects.map(prj => {
            const completedTasks = prj.tasks.filter(t => t.status === 'COMPLETADA').length;
            const totalTasks = prj.tasks.length;

            return (
              <div 
                key={prj.id} 
                className="bg-[#16171d] hover:bg-[#1a1c24] border border-[#272935] hover:border-[#3d4256] rounded-2xl p-5 space-y-4 transition-all shadow-md group flex flex-col justify-between"
              >
                {/* Card Top: Code, Priority, Status */}
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-bold text-sky-400 bg-sky-950/60 border border-sky-500/30 px-2.5 py-0.5 rounded-lg">
                        {prj.code}
                      </span>
                      {getPriorityBadge(prj.priority)}
                      <span className="text-[10px] text-slate-400 bg-slate-800/80 px-2 py-0.5 rounded-md border border-slate-700">
                        {prj.category}
                      </span>
                    </div>
                    {getStatusBadge(prj.status)}
                  </div>

                  {/* Project Name */}
                  <h4 className="text-base font-bold text-white group-hover:text-sky-300 transition-colors leading-snug">
                    {prj.name}
                  </h4>

                  {/* Metadata Row: Client, PM, Dates */}
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-300 pt-1">
                    <div className="flex items-center gap-1.5 truncate">
                      <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="truncate">{prj.clientName}</span>
                    </div>
                    <div className="flex items-center gap-1.5 truncate">
                      <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="truncate">{prj.manager}</span>
                    </div>
                    <div className="flex items-center gap-1.5 truncate col-span-2 text-slate-400 text-[11px]">
                      <Calendar className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                      <span>{prj.startDate} → <strong className="text-white">{prj.targetEndDate}</strong></span>
                    </div>
                  </div>

                  {/* Description preview */}
                  {prj.description && (
                    <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed">
                      {prj.description}
                    </p>
                  )}
                </div>

                {/* Card Bottom: Progress Bar & Action Buttons */}
                <div className="space-y-3 pt-3 border-t border-[#272935]">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400 font-medium">Avance del Proyecto:</span>
                      <span className="font-mono font-bold text-white">
                        <strong className="text-emerald-400 text-sm">{prj.progressPercent}%</strong> ({completedTasks}/{totalTasks} tareas)
                      </span>
                    </div>
                    <div className="w-full bg-[#101115] border border-[#272935] h-2 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          prj.status === 'COMPLETADO' ? 'bg-emerald-500' :
                          prj.status === 'EN_RIESGO' ? 'bg-amber-500' :
                          prj.status === 'RETRASADO' ? 'bg-red-500' : 'bg-sky-500'
                        }`} 
                        style={{ width: `${prj.progressPercent}%` }}
                      />
                    </div>
                  </div>

                  {/* Action Toolbar */}
                  <div className="flex items-center justify-between gap-1.5 pt-1">
                    {/* Left: View detail & Gantt shortcut */}
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setSelectedProjectDetail(prj)}
                        className="px-2.5 py-1.5 rounded-xl bg-[#20222e] hover:bg-[#2b2e3e] text-white text-xs font-semibold flex items-center gap-1 transition-all cursor-pointer"
                        title="Ver Expediente Completo"
                      >
                        <Eye className="w-3.5 h-3.5 text-sky-400" />
                        <span>Detalle</span>
                      </button>

                      <button
                        onClick={() => onSelectProjectForGantt(prj.id)}
                        className="px-2.5 py-1.5 rounded-xl bg-sky-950/60 hover:bg-sky-900/80 text-sky-300 border border-sky-600/40 text-xs font-semibold flex items-center gap-1 transition-all cursor-pointer"
                        title="Abrir Cronograma Gantt de este proyecto"
                      >
                        <Kanban className="w-3.5 h-3.5 text-sky-400" />
                        <span>Gantt</span>
                      </button>
                    </div>

                    {/* Right: PDF, Excel, Edit, Delete */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => exportProjectToPDF(prj)}
                        className="p-1.5 rounded-lg bg-rose-950/40 hover:bg-rose-900/60 text-rose-300 border border-rose-500/30 transition-colors cursor-pointer"
                        title="Descargar Expediente en PDF"
                      >
                        <FileText className="w-3.5 h-3.5" />
                      </button>

                      <button
                        onClick={() => exportProjectToExcel(prj)}
                        className="p-1.5 rounded-lg bg-emerald-950/40 hover:bg-emerald-900/60 text-emerald-300 border border-emerald-500/30 transition-colors cursor-pointer"
                        title="Descargar Detalle y Matriz Gantt en Excel"
                      >
                        <FileSpreadsheet className="w-3.5 h-3.5" />
                      </button>

                      <button
                        onClick={() => {
                          setEditingProject(prj);
                          setIsEditModalOpen(true);
                        }}
                        className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors cursor-pointer"
                        title="Editar Proyecto"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>

                      <button
                        onClick={async () => {
                          const conf = await window.showAppConfirm(`¿Está seguro de eliminar el proyecto "${prj.name}" y todas sus tareas asignadas?`, 'Eliminar Proyecto');
                          if (conf) onDeleteProject(prj.id);
                        }}
                        className="p-1.5 rounded-lg bg-slate-800 hover:bg-red-950/60 text-slate-400 hover:text-red-400 transition-colors cursor-pointer"
                        title="Eliminar Proyecto"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            );
          })}
        </div>

      ) : (

        // TABLE VIEW (EXECUTIVE LIST)
        <div className="bg-[#16171d] border border-[#272935] rounded-2xl overflow-hidden shadow-md">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-300 border-collapse">
              <thead>
                <tr className="border-b border-[#272935] bg-[#111217] text-[10px] font-bold uppercase text-slate-400 font-mono">
                  <th className="py-3.5 px-4">Código</th>
                  <th className="py-3.5 px-4">Nombre del Proyecto</th>
                  <th className="py-3.5 px-4">Cliente / Sede</th>
                  <th className="py-3.5 px-4">Líder PM</th>
                  <th className="py-3.5 px-4 text-center">F. Fin</th>
                  <th className="py-3.5 px-4 text-center">Estado</th>
                  <th className="py-3.5 px-4 text-center">% Avance</th>
                  <th className="py-3.5 px-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#21232e]">
                {filteredProjects.map(prj => {
                  return (
                    <tr key={prj.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 px-4 font-mono font-bold text-sky-400">
                        {prj.code}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-bold text-white">{prj.name}</div>
                        <div className="text-[10px] text-slate-400">{prj.category} • Prioridad: {prj.priority}</div>
                      </td>
                      <td className="py-3 px-4 font-medium text-slate-200">
                        {prj.clientName}
                      </td>
                      <td className="py-3 px-4 text-slate-300">
                        {prj.manager}
                      </td>
                      <td className="py-3 px-4 text-center font-mono text-slate-300">
                        {prj.targetEndDate}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {getStatusBadge(prj.status)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-mono font-bold text-emerald-400 text-sm">
                          {prj.progressPercent}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setSelectedProjectDetail(prj)}
                            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-sky-400 cursor-pointer transition-colors"
                            title="Ver Detalle"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => onSelectProjectForGantt(prj.id)}
                            className="p-1.5 rounded-lg bg-sky-950/60 text-sky-300 hover:bg-sky-900 cursor-pointer transition-colors"
                            title="Plan de Trabajo / Gantt"
                          >
                            <Kanban className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => exportProjectToPDF(prj)}
                            className="p-1.5 rounded-lg bg-rose-950/40 text-rose-300 hover:bg-rose-900/60 cursor-pointer transition-colors"
                            title="Descargar PDF"
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => exportProjectToExcel(prj)}
                            className="p-1.5 rounded-lg bg-emerald-950/40 text-emerald-300 hover:bg-emerald-900/60 cursor-pointer transition-colors"
                            title="Descargar Excel"
                          >
                            <FileSpreadsheet className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingProject(prj);
                              setIsEditModalOpen(true);
                            }}
                            className="p-1.5 rounded-lg bg-amber-950/40 hover:bg-amber-900/60 text-amber-300 border border-amber-500/30 cursor-pointer transition-colors"
                            title="Editar Proyecto"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={async () => {
                              const conf = window.showAppConfirm 
                                ? await window.showAppConfirm(`¿Está seguro de eliminar el proyecto "${prj.name}" (${prj.code}) y todas sus tareas?`, 'Eliminar Proyecto')
                                : window.confirm(`¿Está seguro de eliminar el proyecto "${prj.name}"?`);
                              if (conf) {
                                onDeleteProject(prj.id);
                              }
                            }}
                            className="p-1.5 rounded-lg bg-red-950/40 hover:bg-red-900/70 text-red-400 border border-red-500/30 cursor-pointer transition-colors"
                            title="Eliminar Proyecto"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL: EXPEDIENTE Y DETALLE DEL PROYECTO */}
      {selectedProjectDetail && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#16171e] border border-[#2e3144] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-5 text-white shadow-2xl">
            
            {/* Modal Header */}
            <div className="flex items-start justify-between border-b border-[#2a2c3c] pb-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold text-sky-400 bg-sky-950/60 border border-sky-500/40 px-2.5 py-0.5 rounded-lg">
                    {selectedProjectDetail.code}
                  </span>
                  {getPriorityBadge(selectedProjectDetail.priority)}
                  {getStatusBadge(selectedProjectDetail.status)}
                </div>
                <h3 className="text-lg font-bold text-white">{selectedProjectDetail.name}</h3>
                <p className="text-xs text-slate-400">
                  Unidad: <strong className="text-white">{selectedProjectDetail.businessUnit}</strong> • Cliente: <strong className="text-white">{selectedProjectDetail.clientName}</strong>
                </p>
              </div>
              <button
                onClick={() => setSelectedProjectDetail(null)}
                className="p-1 text-slate-400 hover:text-white rounded-lg bg-[#20222e] cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Top Cards Info */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div className="p-3 rounded-xl bg-[#111217] border border-[#272935] space-y-0.5">
                <span className="text-[10px] text-slate-400 font-bold uppercase">Líder / PM</span>
                <p className="font-bold text-white truncate">{selectedProjectDetail.manager}</p>
              </div>
              <div className="p-3 rounded-xl bg-[#111217] border border-[#272935] space-y-0.5">
                <span className="text-[10px] text-slate-400 font-bold uppercase">Periodo</span>
                <p className="font-bold text-white truncate">{selectedProjectDetail.startDate} al {selectedProjectDetail.targetEndDate}</p>
              </div>
              <div className="p-3 rounded-xl bg-[#111217] border border-[#272935] space-y-0.5">
                <span className="text-[10px] text-slate-400 font-bold uppercase">Avance General</span>
                <p className="font-bold text-emerald-400 font-mono text-base">{selectedProjectDetail.progressPercent}%</p>
              </div>
              <div className="p-3 rounded-xl bg-[#111217] border border-[#272935] space-y-0.5">
                <span className="text-[10px] text-slate-400 font-bold uppercase">Presupuesto</span>
                <p className="font-bold text-white truncate">
                  {selectedProjectDetail.budgetTotal ? `$${selectedProjectDetail.budgetTotal.toLocaleString('es-MX')} ${selectedProjectDetail.budgetCurrency || 'MXN'}` : 'N/A'}
                </p>
              </div>
            </div>

            {/* Scope & Objectives */}
            <div className="p-4 rounded-xl bg-[#111217] border border-[#272935] space-y-2 text-xs">
              <div>
                <span className="text-[10px] text-slate-400 font-bold uppercase block">Descripción del Proyecto:</span>
                <p className="text-slate-300 leading-relaxed mt-0.5">{selectedProjectDetail.description || 'Sin descripción detallada.'}</p>
              </div>
              {selectedProjectDetail.scopeObjectives && (
                <div className="pt-2 border-t border-[#272935]">
                  <span className="text-[10px] text-slate-400 font-bold uppercase block">Alcance y Objetivos de Desempeño:</span>
                  <p className="text-slate-300 leading-relaxed mt-0.5">{selectedProjectDetail.scopeObjectives}</p>
                </div>
              )}
            </div>

            {/* Activities Breakdown Table */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-white uppercase tracking-wider font-mono">
                  📋 Plan de Trabajo & Actividades Asignadas ({selectedProjectDetail.tasks.length})
                </h4>
                <button
                  onClick={() => {
                    const pid = selectedProjectDetail.id;
                    setSelectedProjectDetail(null);
                    onSelectProjectForGantt(pid);
                  }}
                  className="text-xs font-bold text-sky-400 hover:text-sky-300 flex items-center gap-1 cursor-pointer"
                >
                  <Kanban className="w-3.5 h-3.5" />
                  <span>Ver Cronograma Gantt</span>
                </button>
              </div>

              <div className="border border-[#272935] rounded-xl overflow-hidden">
                <table className="w-full text-left text-xs text-slate-300 border-collapse">
                  <thead>
                    <tr className="border-b border-[#272935] bg-[#111217] text-[10px] font-bold uppercase text-slate-400 font-mono">
                      <th className="py-2.5 px-3">WBS</th>
                      <th className="py-2.5 px-3">Actividad / Tarea</th>
                      <th className="py-2.5 px-3">Responsable</th>
                      <th className="py-2.5 px-3 text-center">F. Inicio</th>
                      <th className="py-2.5 px-3 text-center">F. Fin</th>
                      <th className="py-2.5 px-3 text-center">Estado</th>
                      <th className="py-2.5 px-3 text-center">% Avance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#21232e]">
                    {selectedProjectDetail.tasks.map((tsk, i) => (
                      <tr key={tsk.id || i} className="hover:bg-white/[0.02]">
                        <td className="py-2.5 px-3 font-mono text-slate-400">{tsk.wbsCode || `${i + 1}`}</td>
                        <td className="py-2.5 px-3">
                          <div className="font-semibold text-white">
                            {tsk.isMilestone ? `🔷 [HITO] ${tsk.name}` : tsk.name}
                          </div>
                          {tsk.deliverable && <div className="text-[10px] text-slate-400">Entregable: {tsk.deliverable}</div>}
                        </td>
                        <td className="py-2.5 px-3">{tsk.assignee}</td>
                        <td className="py-2.5 px-3 text-center font-mono text-slate-400">{tsk.startDate}</td>
                        <td className="py-2.5 px-3 text-center font-mono text-slate-400">{tsk.endDate}</td>
                        <td className="py-2.5 px-3 text-center">
                          <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${
                            tsk.status === 'COMPLETADA' ? 'bg-emerald-950 text-emerald-300' :
                            tsk.status === 'EN_PROCESO' ? 'bg-sky-950 text-sky-300' : 'bg-slate-800 text-slate-300'
                          }`}>
                            {tsk.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center font-mono font-bold text-emerald-400">{tsk.progressPercent}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Bottom: Download & Action buttons */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-[#2a2c3c]">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => exportProjectToPDF(selectedProjectDetail)}
                  className="px-3.5 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md cursor-pointer transition-all"
                >
                  <FileText className="w-4 h-4" />
                  <span>PDF</span>
                </button>

                <button
                  onClick={() => exportProjectToExcel(selectedProjectDetail)}
                  className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md cursor-pointer transition-all"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  <span>Excel con Gantt</span>
                </button>

                <button
                  onClick={() => {
                    const prjToEdit = selectedProjectDetail;
                    setSelectedProjectDetail(null);
                    setEditingProject(prjToEdit);
                    setIsEditModalOpen(true);
                  }}
                  className="px-3.5 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md cursor-pointer transition-all"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Editar Proyecto</span>
                </button>

                <button
                  onClick={async () => {
                    const conf = window.showAppConfirm 
                      ? await window.showAppConfirm(`¿Está seguro de eliminar el proyecto "${selectedProjectDetail.name}" (${selectedProjectDetail.code}) y todas sus actividades?`, 'Eliminar Proyecto')
                      : window.confirm(`¿Está seguro de eliminar el proyecto "${selectedProjectDetail.name}"?`);
                    if (conf) {
                      const idToDelete = selectedProjectDetail.id;
                      setSelectedProjectDetail(null);
                      onDeleteProject(idToDelete);
                    }
                  }}
                  className="px-3.5 py-2 bg-red-600/30 hover:bg-red-600 text-red-300 hover:text-white border border-red-500/40 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md cursor-pointer transition-all"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Eliminar Proyecto</span>
                </button>
              </div>

              <button
                onClick={() => setSelectedProjectDetail(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold cursor-pointer"
              >
                Cerrar
              </button>
            </div>

          </div>
        </div>
      )}

      {/* MODAL: NUEVO / EDITAR PROYECTO */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#16171e] border border-[#2e3144] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4 text-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#2a2c3c] pb-3">
              <h3 className="text-base font-bold text-white">
                {editingProject.id && projects.some(p => p.id === editingProject.id) ? 'Editar Proyecto' : 'Registrar Nuevo Proyecto'}
              </h3>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveProjectForm} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Código del Proyecto *</label>
                  <input
                    type="text"
                    required
                    placeholder="PRJ-BAJ-2025-01"
                    value={editingProject.code || ''}
                    onChange={(e) => setEditingProject({ ...editingProject, code: e.target.value.toUpperCase() })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-mono"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-slate-300 font-bold mb-1">Nombre del Proyecto *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Mantenimiento Mayor de Tina Pirolítica"
                    value={editingProject.name || ''}
                    onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-bold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Cliente / Planta Operativa *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Mazda Salamanca"
                    value={editingProject.clientName || ''}
                    onChange={(e) => setEditingProject({ ...editingProject, clientName: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Líder PM / Responsable *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Ing. Alejandro Fuentes"
                    value={editingProject.manager || ''}
                    onChange={(e) => setEditingProject({ ...editingProject, manager: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Categoría</label>
                  <select
                    value={editingProject.category || 'MANTENIMIENTO'}
                    onChange={(e) => setEditingProject({ ...editingProject, category: e.target.value as any })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  >
                    <option value="MANTENIMIENTO">Mantenimiento</option>
                    <option value="AUTOMATIZACION">Automatización</option>
                    <option value="OBRA_CIVIL">Obra Civil</option>
                    <option value="HSEQ">HSEQ / Seguridad</option>
                    <option value="SERVICIOS_ESPECIALIZADOS">Servicios Especializados</option>
                    <option value="LOGISTICA">Logística</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Estado</label>
                  <select
                    value={editingProject.status || 'POR_INICIAR'}
                    onChange={(e) => setEditingProject({ ...editingProject, status: e.target.value as any })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-bold"
                  >
                    <option value="POR_INICIAR">Por Iniciar</option>
                    <option value="EN_TIEMPO">En Tiempo</option>
                    <option value="EN_RIESGO">En Riesgo</option>
                    <option value="RETRASADO">Retrasado</option>
                    <option value="COMPLETADO">Completado</option>
                    <option value="PAUSADO">Pausado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Prioridad</label>
                  <select
                    value={editingProject.priority || 'ALTA'}
                    onChange={(e) => setEditingProject({ ...editingProject, priority: e.target.value as any })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  >
                    <option value="CRITICA">Crítica</option>
                    <option value="ALTA">Alta</option>
                    <option value="MEDIA">Media</option>
                    <option value="BAJA">Baja</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Fecha de Inicio *</label>
                  <input
                    type="date"
                    required
                    value={editingProject.startDate || ''}
                    onChange={(e) => setEditingProject({ ...editingProject, startDate: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Fecha Compromiso (Fin) *</label>
                  <input
                    type="date"
                    required
                    value={editingProject.targetEndDate || ''}
                    onChange={(e) => setEditingProject({ ...editingProject, targetEndDate: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">% Avance (0 - 100)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={editingProject.progressPercent ?? 0}
                    onChange={(e) => setEditingProject({ ...editingProject, progressPercent: parseInt(e.target.value) || 0 })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Descripción y Justificación</label>
                <textarea
                  rows={2}
                  placeholder="Detalles del alcance técnico, equipos involucrados y objetivos..."
                  value={editingProject.description || ''}
                  onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                  className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Objetivos de Desempeño / Entregables</label>
                <input
                  type="text"
                  placeholder="Ej: Disponibilidad 99.5%, Certificado NOM-002, Acta firmada"
                  value={editingProject.scopeObjectives || ''}
                  onChange={(e) => setEditingProject({ ...editingProject, scopeObjectives: e.target.value })}
                  className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#2a2c3c]">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl font-bold cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-red-600 hover:bg-red-500 text-white rounded-xl font-bold cursor-pointer shadow-md"
                >
                  Guardar Proyecto
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
